import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { connect } from 'react-redux';
import Spinner from 'react-spinkit';
import { branch, compose, lifecycle, renderComponent, renderNothing, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { handleInvalidForm, isBusinessAdmin, isBusinessUser } from '../../../../../services';
import { businessActions } from '../../../../../state/modules/business';
import { dataGovActions } from '../../../../../state/modules/data-gov';
import { userActions } from '../../../../../state/modules/user';
import { FormSubmissionMessage, Icon, TypeaheadField } from '../../../../components';

const AsflField = branch(
  ({ user }) => !isAfslEditAllowed(user),
  renderNothing
)(({
  searchText,
  datagov,
  searchAfsLicensees,
  onAfsLicenseeSelected,
  setSearchText,
  validation,
  user,
  updatingAfsl
}) => [
    <TypeaheadField
      key='afsl-field'
      label='Search for licence holder or AFSL'
      icon='search-large'
      searchText={searchText}
      isLoading={datagov.isFetching}
      emptyLabel={isInValidLicenceNum(searchText) ?
        "Please enter correct licence number (6 numbers)" : 'No matches'}
      options={datagov ? datagov.afsLicensees : []}
      labelKey={renderAfsLicensee}
      renderMenuItemChildren={renderAfsLicensee}
      onSearch={(v) => searchAfsLicensees(v)}
      onChange={(v) => onAfsLicenseeSelected(v)}
      onInputChange={(v) => setSearchText(v)}
    />,
    validation.afsl ?
      null :
      <div
        key='validation'
        aria-invalid='true'
        className='has-error'>
        <span className='help-block'>
          Please select valid AFSL number from the search
      </span>
      </div>,
    user && isAfslEditAllowed(user) ?
      <Button
        key='submit-button'
        type='submit'
        className='half'>
        {
          updatingAfsl ?
            <Spinner name='circle' className='spinner' fadeIn='none' /> :
            'Update'
        }
      </Button> :
      null
  ]);

const CurrentAfsl = branch(
  ({ afsLicence, editingAfsl }) => editingAfsl || !afsLicence,
  renderComponent(AsflField)
)(({
  afsLicence,
  business,
  user,
  removeAfsl,
  removingAfsl,
  editAfls
}) => (
    [
      <p key='current-afsl' className='current-afsl'>
        {
          afsLicence ?
            `${he.decode(afsLicence.afsLicenceHolder)} - (${afsLicence.afsLicenceNumber})` :
            null
        }
        {
          isAfslEditAllowed(user) ?
            <Button
              bsStyle='plain'
              onClick={() => removeAfsl()}>
              {
                removingAfsl ?
                  <Spinner name='circle' className='spinner' fadeIn='none' /> :
                  <Icon icon='close' />
              }
            </Button> :
            null
        }
      </p>,
      isAfslEditAllowed(user) ?
        <Button
          key='edit-button'
          className='half'
          onClick={() => editAfls()}>
          Edit
        </Button> :
        null
    ]
  )
);

const AfsSetting = ({
  businessUser,
  business,
  removeAfsl,
  removingAfsl,
  afsLicence,
  user,
  showSubmissionMessage,
  submissionMessage,
  localError,
  closeSubmissionMessage,
  focusMessage,
  resetFocusMessage,
  searchText,
  datagov,
  onAfsLicenseeSelected,
  setSearchText,
  updatingAfsl,
  validation,
  searchAfsLicensees,
  updateAfsl,
  tooltip,
  editingAfsl,
  editAfls,
  setOverlayRef
}) => (
    <div className='profile--afsl-setting'>
      <div className='inner'>
        <h2>
          <Icon icon='user' /> Australian financial services licence (AFSL)
        </h2>
        <form noValidate onSubmit={updateAfsl}>
          <FormSubmissionMessage
            showSubmissionMessage={showSubmissionMessage}
            submissionMessage={submissionMessage}
            error={localError}
            stopFocusingMessage={resetFocusMessage}
            hideSubmissionMessage={closeSubmissionMessage} />
          <Row>
            <Col xs={12}>
              <fieldset className='text-center'>
                <Row className='text-center'>
                  <Col xs={12}>
                    <OverlayTrigger ref={(ref) => setOverlayRef(ref)} placement='bottom' overlay={tooltip}>
                      <Button bsStyle='plain'>
                        <Icon icon='help' className='hide-on-ie' /> <span>What's this?</span>
                      </Button>
                    </OverlayTrigger>
                  </Col>
                  <Col xs={12}>
                    <CurrentAfsl {...{
                      afsLicence,
                      business,
                      user,
                      removeAfsl,
                      removingAfsl,
                      searchText,
                      datagov,
                      searchAfsLicensees,
                      onAfsLicenseeSelected,
                      setSearchText,
                      validation,
                      updatingAfsl,
                      editingAfsl,
                      editAfls
                    }} />
                  </Col>
                </Row>
              </fieldset>
            </Col>
          </Row>
        </form>
      </div>
    </div>
  );

const NoAfslBlock = () => (
  <div className='profile--afsl-setting'>
    <div className='inner'>
      <h2>
        <Icon icon='user' /> Australian financial services licence (AFSL)
      </h2>
      <p>
        Your business administrator has not yet identified the AFSL your business operates under. Please contact them directly to have this added to your account.
      </p>
    </div>
  </div>
)

const mapStateToProps = (state) => ({
  isUserUpdating: state.user.isUpdating,
  isBusinessUpdating: state.business.isUpdatingAfsl,
  datagov: state.datagov,
  error: state.user.error || state.business.afslError
});

const mapDispatchToProps = (dispatch) => ({
  searchAfsLicensees: bindActionCreators(dataGovActions.dataGov.licensees.get, dispatch),
  addUserAfsl: bindActionCreators(userActions.user.addAfsl, dispatch),
  addBusinessAfsl: bindActionCreators(businessActions.business.addAfsLicence, dispatch),
  removeUserAfsl: bindActionCreators(userActions.user.removeAfsl, dispatch),
  removeBusinessAfsl: bindActionCreators(businessActions.business.removeAfsl, dispatch),
  getUser: bindActionCreators(userActions.user.getCurrent, dispatch)
});

const isAfslEditAllowed = fp.overSome([
  isBusinessAdmin,
  fp.negate(isBusinessUser)
]);

// Valid licence number must contains 6 numbers
const validLicenceNumLength = 6;

// Check if the search query is an invalid licence number
// Return false when search query is not number
// or search query is a valid licence number
const isInValidLicenceNum = (searchText) => {
  return  /^\d+$/.test(searchText) && searchText.length !== validLicenceNumLength;
}

const renderAfsLicensee = (option) =>
  he.decode(`${option.businessName} - (${option.number})`);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({
    user
  }) => ({
    businessUser: isBusinessUser(user),
    businessAdmin: isBusinessAdmin(user)
  })),
  withStateHandlers(
    {
      selectedAfsLicenceNumber: null,
      editingAfsl: false,
      updatingAfsl: false,
      removingAfsl: false,
      showSubmissionMessage: false,
      focusMessage: false,
      submissionMessage: '',
      localError: null,
      validation: {
        afsl: true
      },
      overlayRef: null
    },
    {
      onAfsLicenseeSelected: () => (value) => ({
        selectedAfsLicenceNumber: value && value.length > 0 ?
          value[0] :
          null
      }),
      editAfls: () => () => ({
        editingAfsl: true
      }),
      setLocalError: () => (error) => ({
        localError: error
      }),
      onUpdateAfls: () => ({
        validation,
        afsLicence
      }) => ({
        validation: {
          ...validation,
          afsl: true
        },
        updatingAfsl: true,
        showSubmissionMessage: false,
        localError: null,
        submissionMessage: afsLicence.businessId ?
          'Your business AFSL has been updated.' :
          'Your AFSL has been updated.'
      }),
      onInvalidAfsl: () => (validation) => ({
        validation: {
          ...validation,
          afsl: false
        }
      }),
      onRemoveAfsl: () => (afsLicence) => ({
        removingAfsl: true,
        showSubmissionMessage: false,
        localError: null,
        submissionMessage: afsLicence.businessId ?
          'Your business AFSL has been removed.' :
          'Your AFSL has been removed.'
      }),
      closeSubmissionMessage: ({ showSubmissionMessage }) => () => ({
        showSubmissionMessage: false,
        localError: null
      }),
      resetFocusMessage: ({ focusMessage }) => () => ({
        focusMessage: false
      }),
      setSearchText: ({ searchText }) => (value) => ({
        searchText: value
      }),
      onUpdatingFinish: () => () => ({
        editingAfsl: false,
        updatingAfsl: false,
        removingAfsl: false,
        focusMessage: true,
        showSubmissionMessage: true
      }),
      setOverlayRef: () => (ref) => ({overlayRef: ref})
    }
  ),
  withProps(({
    overlayRef
  }) => ({
    tooltip: (
      <Tooltip id='afsl-tooltip'>
        <Button onClick={() => overlayRef.hide()} className='pull-right full-width close-overlay' bsStyle='plain'>
          <Icon icon='close' />
        </Button>
        <p>
          <strong>
            Australian Financial Services Licence (ASFL)
          </strong>
        </p>
        <p>
          Your AFSL (Australian Financial Services Licence) is a licence given by ASIC
          that allows people or companies to legally carry on a financial services
          business, including selling, advising or dealing in financial products.
        </p>
      </Tooltip>
    )
  })),
  withHandlers({
    updateAfsl: ({
      selectedAfsLicenceNumber,
      validation,
      businessAdmin,
      user,
      addBusinessAfsl,
      addUserAfsl,
      onUpdateAfls,
      onInvalidAfsl
    }) => (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (selectedAfsLicenceNumber) {
        const afsLicence = {
          userId: user.id,
          afsLicenceNumber: selectedAfsLicenceNumber.number,
          afsLicenceHolder: selectedAfsLicenceNumber.businessName,
          businessId: businessAdmin && businessAdmin.businessId,
          noRedirect: true
        };

        onUpdateAfls({ validation, afsLicence });

        if (afsLicence.businessId) {
          addBusinessAfsl(afsLicence);
        }
        else {
          addUserAfsl(afsLicence);
        }
      }
      else {
        onInvalidAfsl(validation);
      }
      handleInvalidForm(e.target);
    },
    removeAfsl: ({
      afsLicence,
      removeBusinessAfsl,
      removeUserAfsl,
      onRemoveAfsl
    }) => () => {
      onRemoveAfsl(afsLicence);

      if (afsLicence.businessId) {
        removeBusinessAfsl(afsLicence.businessId, afsLicence.afsLicenceNumber);
      }
      else {
        removeUserAfsl(afsLicence.afsLicenceNumber);
      }
    }
  }),
  lifecycle({
    componentWillReceiveProps(nextProps) {
      const {
        isUserUpdating: wasUserUpdating,
        isBusinessUpdating: wasBusinessUpdating
      } = this.props;
      const {
        updatingAfsl,
        removingAfsl,
        onUpdatingFinish,
        isUserUpdating,
        isBusinessUpdating,
        error,         
        setLocalError  
      } = nextProps;
      
      const updateFinished = (updatingAfsl || removingAfsl) &&
        ((!isUserUpdating && wasUserUpdating) ||
          (!isBusinessUpdating && wasBusinessUpdating));
          
      if (updateFinished) {
        if (error) {
          setLocalError(error);
        }
        onUpdatingFinish();
      }
    }
  }),
  branch(
    ({ businessUser, afsLicence }) => businessUser && !afsLicence,
    renderComponent(NoAfslBlock)
  )
)(AfsSetting);


// WEBPACK FOOTER //
// ./src/containers/pages/app/professional-profile/partials/afsl-setting.js